import { useCallback, useLayoutEffect, useState } from 'react';

interface ObservedElementSize<T extends HTMLElement> {
  height: number;
  present: boolean;
  ref: (element: T | null) => void;
  width: number;
}

export function useObservedElementSize<T extends HTMLElement>(enabled: boolean): ObservedElementSize<T> {
  const [element, setElement] = useState<T | null>(null);
  const [size, setSize] = useState({ height: 0, width: 0 });
  const ref = useCallback((nextElement: T | null) => setElement(nextElement), []);

  useLayoutEffect(() => {
    if (!enabled || !element) {
      setSize({ height: 0, width: 0 });
      return;
    }

    const updateSize = ({ height, width }: { height: number; width: number }) => {
      setSize({
        height: Math.max(0, height),
        width: Math.max(0, width),
      });
    };

    updateSize(element.getBoundingClientRect());

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      updateSize(entries[0]?.contentRect ?? element.getBoundingClientRect());
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, enabled]);

  return { present: element !== null, ref, ...size };
}
